import { unwrapApiResponse } from '@/utils/api';

export type PaginatedBody<T> = {
    data?: T[];
    pagination?: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
        hasNextPage?: boolean;
        hasPreviousPage?: boolean;
    };
};

/** Lê lista paginada após `api.get` (axios). */
export function parsePaginatedList<T>(response: unknown): {
    items: T[];
    totalPages: number;
    totalItems: number;
} {
    const body = unwrapApiResponse<PaginatedBody<T> | T[]>(response);
    if (Array.isArray(body)) {
        return { items: body, totalPages: 1, totalItems: body.length };
    }
    const items = Array.isArray(body?.data) ? body.data! : [];
    const p = body?.pagination;
    const totalItems = p?.totalItems ?? items.length;
    const totalPages = Math.max(1, p?.totalPages ?? Math.ceil(totalItems / (p?.itemsPerPage || 20)));
    return { items, totalPages, totalItems };
}

/** GET `readings/continue` — mesmo envelope que `ContinueLendo.tsx` (`response.data` com `data` + `pagination`). */
export function parseContinueReadingList<T = unknown>(response: unknown): {
    items: T[];
    totalPages: number;
    totalItems: number;
} {
    const envelope = unwrapApiResponse<Record<string, unknown> | T[] | null | undefined>(response);
    if (Array.isArray(envelope)) {
        return { items: envelope, totalPages: 1, totalItems: envelope.length };
    }
    if (!envelope || typeof envelope !== 'object') {
        return { items: [], totalPages: 1, totalItems: 0 };
    }
    const inner = envelope as PaginatedBody<T>;
    let p = inner.pagination;
    let rows: unknown = inner.data;
    if (rows && typeof rows === 'object' && !Array.isArray(rows) && 'data' in (rows as object)) {
        const layer = rows as { data?: T[]; pagination?: PaginatedBody<T>['pagination'] };
        if (Array.isArray(layer.data)) {
            rows = layer.data;
            if (layer.pagination) p = layer.pagination;
        }
    }
    const items = Array.isArray(rows) ? (rows as T[]) : [];
    const totalItems = p?.totalItems ?? items.length;
    const totalPages = Math.max(1, p?.totalPages ?? Math.ceil(totalItems / (p?.itemsPerPage || 20)));
    return { items, totalPages, totalItems };
}

/** Normaliza item de GET `favorites` para o formato esperado pelos cards da coleção. */
export function normalizeFavoriteObra(raw: any) {
    const ult = raw.ultimo_capitulo_lido;
    const pub = raw.ultimo_capitulo_publicado;
    const prox = raw.proximo_lido;
    const formatoStr = typeof raw.formato === 'string' ? raw.formato : raw.formato?.nome;
    const ultimo_lido = ult
        ? { cap_num: ult.cap_num, numero: ult.cap_num, cap_id: ult.cap_id, cap_nome: ult.cap_nome }
        : null;
    const ultimo_publicado = pub
        ? { cap_id: pub.cap_id, cap_num: pub.cap_num, cap_nome: pub.cap_nome ?? String(pub.cap_num ?? '') }
        : null;
    const totalCapRaw = raw.total_capitulos ?? raw._count?.capitulos;
    const total_capitulos = typeof totalCapRaw === 'number' && totalCapRaw > 0 ? totalCapRaw : 0;
    const ultNum = ultimo_lido?.cap_num ?? ultimo_lido?.numero ?? 0;
    const total_lidos =
        typeof raw.total_lidos === 'number' && raw.total_lidos >= 0 ? raw.total_lidos : ultNum;
    const proximo_lido =
        prox && prox.cap_id != null
            ? {
                  cap_id: prox.cap_id,
                  cap_num: prox.cap_num ?? prox.numero,
                  numero: prox.numero ?? prox.cap_num,
                  cap_nome: prox.cap_nome,
              }
            : null;
    return {
        ...raw,
        id: raw.id,
        nome: raw.nome,
        imagem: raw.imagem,
        formato: formatoStr ? { id: raw.formt_id ?? 0, nome: formatoStr } : undefined,
        ultimo_lido,
        ultimo_publicado,
        total_capitulos,
        total_lidos,
        proximo_lido,
    };
}
